/**
 * Was am Einsatzort noch zu finden ist.
 *
 * Der Zeitdruck des Spiels steckt vollstaendig hier: Dieselbe Fahrt endet je
 * nach Ankunftszeit in einer Rettung, einer Bergung oder in leerer See. Der
 * Spieler bekommt nie den exakten Wert zu sehen, sondern nur eine Schaetzung —
 * daraus entsteht die Spannung.
 */

export type OutcomeTier = 'rescue' | 'salvage' | 'cold' | 'nothing';

/** Alle Stufen, von der besten zur schlechtesten. Exportiert, damit Tests und
 * Bildzuordnung gegen die Liste statt gegen eine Aufzaehlung von Hand pruefen. */
export const OUTCOME_TIERS: readonly OutcomeTier[] = ['rescue', 'salvage', 'cold', 'nothing'];

/** Reihenfolge von "am meisten zu holen" nach "nichts mehr". */
export const OUTCOME_ORDER: readonly OutcomeTier[] = ['rescue', 'salvage', 'cold', 'nothing'];

/**
 * Zeitfenster eines Vorfalls, in Stunden ab dem Vorfall.
 * Jede Grenze ist der Zeitpunkt, an dem die jeweilige Stufe endet.
 */
export interface OutcomeWindows {
  /** Bis hierhin gibt es Ueberlebende. */
  readonly rescueHours: number;
  /** Bis hierhin liegen noch bergungsfaehige Teile beisammen. */
  readonly salvageHours: number;
  /** Bis hierhin sind wenigstens Spuren nachweisbar. */
  readonly coldHours: number;
}

export function outcomeFor(elapsedHours: number, windows: OutcomeWindows): OutcomeTier {
  if (elapsedHours <= windows.rescueHours) return 'rescue';
  if (elapsedHours <= windows.salvageHours) return 'salvage';
  if (elapsedHours <= windows.coldHours) return 'cold';
  return 'nothing';
}

/** Wie viele Hinweise eine Stufe liefert. */
export const CLUES_PER_TIER: Readonly<Record<OutcomeTier, number>> = {
  rescue: 2,
  salvage: 2,
  cold: 1,
  nothing: 0,
};

/**
 * Welche Art von Beweisen eine Stufe ueberhaupt hergeben kann.
 *
 * Das ist die zentrale Kopplung des Spiels: Ueberlebende koennen erzaehlen,
 * aber nichts vorlegen. Ein Wrack gibt Gegenstaende her, aber keine Aussagen.
 * Und wo nichts mehr treibt, bleiben nur noch Messwerte und die Spuren
 * derjenigen, die vor uns hier waren.
 *
 * Daraus entsteht das Dilemma, um das sich das Spiel dreht: Harte Belege
 * liegen ausschliesslich dort, wo niemand mehr zu retten war. Wer die Wahrheit
 * will, muss zu spaet kommen — und zwar absichtlich.
 */
export const EVIDENCE_KINDS_PER_TIER: Readonly<Record<OutcomeTier, readonly string[]>> = {
  // Wer lebend geborgen wird, kann berichten. Mehr nicht.
  rescue: ['witness_testimony'],
  // Am Wrack liegen Gegenstaende, Aufzeichnungen und die Spuren der Besatzung.
  salvage: ['physical_artifact', 'missing_person_trace', 'instrument_reading'],
  // Ist alles fortgetrieben, bleiben Messwerte und fremde Anwesenheit.
  cold: ['anomaly_signature', 'third_party_interference'],
  nothing: [],
};

/**
 * Ansehen, das eine Stufe einbringt. Eine Rettung zaehlt deutlich mehr als
 * eine Bergung — davon haengt das Budget der Basis ab.
 */
export const REPUTATION_PER_TIER: Readonly<Record<OutcomeTier, number>> = {
  rescue: 12,
  salvage: 5,
  cold: 1,
  nothing: -2,
};

/**
 * Welche Hinweisqualitaet eine Stufe zulaesst.
 *
 * Wichtig fuers Spielgefuehl: Auch die spaeten Stufen tragen zur Aufklaerung
 * bei. Wer zu spaet kommt, verliert Menschen und Ansehen — aber die Kampagne
 * laeuft nie in eine Sackgasse, weil selbst eine kalte Spur noch etwas hergibt.
 */
export function tierYieldsEvidence(tier: OutcomeTier): boolean {
  return tier !== 'nothing';
}

/** Wie sicher sich die Station ihrer Einschaetzung ist. */
export type ForecastConfidence = 'high' | 'medium' | 'low';

export interface TierForecast {
  /** Die wahrscheinlichste Stufe nach dem, was die Station weiss. */
  readonly tier: OutcomeTier;
  readonly confidence: ForecastConfidence;
  /** Die andere Moeglichkeit, wenn die Schaetzung kippen koennte. */
  readonly alternative: OutcomeTier | null;
}

/**
 * Einschaetzung vor dem Auslaufen — ausdruecklich **keine** Zusage.
 *
 * Gerechnet wird mit den *geschaetzten* Fenstern des Vorfalls, nicht mit den
 * wahren. Die Station weiss nicht, wie lange Menschen in dieser See durchhalten;
 * sie schaetzt. Deshalb kann diese Vorhersage danebenliegen — und genau darin
 * liegt die Spannung. Waere sie immer richtig, waere die Fahrt nur noch das
 * Abarbeiten einer Rechnung.
 *
 * `uncertaintyHours` ist die Streuung, mit der die Station rechnet. Kippt die
 * Stufe innerhalb dieser Spanne, sinkt das Vertrauen und die zweite
 * Moeglichkeit wird mitgenannt.
 */
export function forecastTier(
  estimatedArrivalHours: number,
  estimatedWindows: OutcomeWindows,
  uncertaintyHours = 0,
): TierForecast {
  const tier = outcomeFor(estimatedArrivalHours, estimatedWindows);

  if (uncertaintyHours <= 0) {
    return { tier, confidence: 'high', alternative: null };
  }

  const earlier = outcomeFor(
    Math.max(0, estimatedArrivalHours - uncertaintyHours),
    estimatedWindows,
  );
  const later = outcomeFor(estimatedArrivalHours + uncertaintyHours, estimatedWindows);

  if (earlier === tier && later === tier) {
    return { tier, confidence: 'high', alternative: null };
  }

  // Die schlechtere der beiden Moeglichkeiten ist die, auf die man sich
  // einstellen sollte — deshalb wird sie als Alternative genannt.
  const alternative = later !== tier ? later : earlier;
  const distanceToFlip = Math.min(
    ...[windowBoundaries(estimatedWindows)]
      .flat()
      .map((boundary) => Math.abs(boundary - estimatedArrivalHours)),
  );

  return {
    tier,
    confidence: distanceToFlip < uncertaintyHours * 0.4 ? 'low' : 'medium',
    alternative,
  };
}

function windowBoundaries(windows: OutcomeWindows): number[] {
  return [windows.rescueHours, windows.salvageHours, windows.coldHours];
}

/** Verbleibende Stunden bis die naechstschlechtere Stufe eintritt. */
export function hoursUntilDowngrade(
  elapsedHours: number,
  windows: OutcomeWindows,
): number | null {
  const boundaries = [windows.rescueHours, windows.salvageHours, windows.coldHours];
  for (const boundary of boundaries) {
    if (elapsedHours <= boundary) return boundary - elapsedHours;
  }
  return null;
}
